import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from 'modules/auth/context/AuthContextV2';
import Button from 'components/v2/button/Button';
import { cn } from 'utils/helpers';
import { useGetAvailableContactsQuery } from 'services/event.service';
import { Contact } from 'modules/profile/types/types';
import UpdatesCard from '../UpdatesCard';
import EmojiAvatar from '../EmojiAvatar';

type Props = {
  className?: string;
  handleDismiss: (eventId: string, expiresAt?: Date) => void;
  isShadows?: boolean;
};

const FriendsCard = ({ handleDismiss, isShadows = true, className }: Props) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: availableContacts } = useGetAvailableContactsQuery();
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(
    new Set(),
  );

  if (!user) return null;

  const toggleContactSelection = (contactId: string) => {
    setSelectedContacts((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(contactId)) {
        newSelected.delete(contactId);
      } else {
        newSelected.add(contactId);
      }
      return newSelected;
    });
  };

  const handleNavigate = () => {
    const selectedContactsIds = Array.from(selectedContacts).join(',');
    navigate(
      `/lets-meet?selectedContactsIds=${encodeURIComponent(selectedContactsIds)}`,
    );
  };
  const filteredAvailableContacts = useMemo(() => {
    return availableContacts?.data?.filter((contact) => {
      return contact._doc?.userId !== user?.id;
    });
  }, [availableContacts, user]);

  return (
    <>
      {isShadows && (
        <div className="absolute bg-transparent w-full h-full shadow-[0px_50px_110px_-61px_#AAA8FF] rounded-[6rem] z-[-1]"></div>
      )}
      <UpdatesCard
        isShadows={isShadows}
        title={`You have (${filteredAvailableContacts?.length}) friends free now`}
        isEmojiIcon={false}
        className={cn('shadow-none', className)}
        description="Copy your customer profile link below and share your profile with others to join you on Caleido for faster meetup coordination"
        infoChildren={
          <div className="w-full flex flex-col gap-2 overflow-hidden">
            <div className="text-[0.875rem]">Swipe to see all</div>

            <div
              className="w-[calc(100svw-5rem)] overflow-x-auto"
              onTouchMove={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="w-max flex items-start gap-[1rem]">
                {filteredAvailableContacts?.map(
                  (contact: Contact, index: number) => {
                    const contactName = contact._doc?.name
                      ?.split(' ')
                      .slice(0, 2)
                      .join(' ');

                    const contactAvailabilityTime =
                      dayjs(contact.endDate).minute() === 0
                        ? dayjs(contact.endDate).format('ha')
                        : dayjs(contact.endDate).format('h:mma');

                    const image = `${process.env.REACT_APP_BASE_URL}/users/profile-pic/${contact._doc?.userId}`;

                    return (
                      <div
                        className={`flex flex-col py-2 px-0 min-w-[4rem] w-full rounded-[1rem] transition-all duration-300 justify-center items-center gap-1 ${selectedContacts.has(contact._doc?.phoneNumber || '') ? 'bg-[#434572]' : ''}`}
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleContactSelection(
                            contact._doc?.phoneNumber || '',
                          );
                        }}
                      >
                        <EmojiAvatar
                          size="medium-large"
                          fullName={contact._doc?.name || ''}
                          image={image}
                          isOnline={true}
                        />
                        <div className="flex flex-col gap-[0.125rem]">
                          <div className="text-center text-[0.875rem]">
                            {contactName}
                          </div>
                          <div className="text-center text-[0.75rem] text-[#ABE49C]">
                            {`until ${contactAvailabilityTime}`}
                          </div>
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          </div>
        }
        actionsChildren={
          <>
            <Button
              className="min-w-[65%] py-3 text-[0.875rem] font-semibold"
              variant="primary"
              onClick={handleNavigate}
              disabled={selectedContacts.size === 0}
            >
              Tap to hang out
            </Button>
            <Button
              variant="secondary"
              className="py-3 text-[0.875rem]"
              onClick={() => {
                handleDismiss('friends', dayjs().add(10, 'minutes').toDate());
              }}
            >
              Dismiss
            </Button>
          </>
        }
      />
    </>
  );
};

export default FriendsCard;
